import { useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { FrameWithStickyTitle } from 'components/BrandSlides/BrandSlides.jsx';
import { ensureArray, safeMap, safeGetProp, ensureString } from 'utils/safeArray';

export default function ListSlide({ title, subtitle, list_items = [], layout, variant, accent_color }) {
  const safeListItems = ensureArray(list_items);
  const layoutValue = layout ? String(layout).toLowerCase() : null;
  const variantValue = variant ? String(variant).toLowerCase() : null;
  // Determine two-column mode: explicit variant overrides; else explicit layout keywords; else auto (>=4 → two columns)
  let twoColumn;
  if (variantValue) {
    twoColumn = variantValue === 'twocolumn';
  } else if (layoutValue && ['horizontal', 'twocolumn', 'two', '2column', 'two column'].includes(layoutValue)) {
    twoColumn = true;
  } else if (layoutValue && ['onecolumn', 'vertical', 'one', 'single'].includes(layoutValue)) {
    twoColumn = false;
  } else {
    twoColumn = safeListItems.length >= 4;
  }

  const spread = !twoColumn && safeListItems.length > 0 && safeListItems.length <= 3;
  const containerClass = twoColumn
    ? 'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 min-h-[30vh]'
    : `flex flex-col gap-4 md:gap-6 ${spread ? 'justify-between' : ''} min-h-[30vh]`;

  const [iconMap, setIconMap] = useState({});
  const iconNames = useMemo(() =>
    Array.from(new Set(safeMap(safeListItems, it => safeGetProp(it, 'icon')).filter(Boolean))),
  [safeListItems]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const solid = await import('@heroicons/react/24/solid');
        let outline = {};
        try {
          outline = await import('@heroicons/react/24/outline');
        } catch {
          outline = {};
        }
        const map = {};
        for (const name of iconNames) {
          map[name] = solid[name] || outline[name] || null;
        }
        if (active) setIconMap(map);
      } catch {
        if (active) setIconMap({});
      }
    })();
    return () => { active = false; };
  }, [iconNames]);

  return (
    <FrameWithStickyTitle title={title} subtitle={subtitle} accentColor={accent_color}>
      <div className={containerClass}>
        {safeMap(safeListItems, (item, i) => (
          <div key={i} className="grid grid-cols-[40px_1fr] md:grid-cols-[56px_1fr] gap-3 md:gap-4 items-center">
            <div className="flex items-center justify-center">
              {safeGetProp(item, 'icon') ? (
                (() => {
                  const IconComp = iconMap[safeGetProp(item, 'icon')];
                  return IconComp ? (
                    <IconComp className="h-10 w-10 md:h-14 md:w-14" style={{ color: accent_color || '#c00000' }} />
                  ) : (
                    <CheckCircleIcon className="h-10 w-10 md:h-14 md:w-14" style={{ color: accent_color || '#c00000' }} />
                  );
                })()
              ) : safeGetProp(item, 'icon_url') ? (
                <img src={ensureString(safeGetProp(item, 'icon_url'))} alt="" className="h-10 w-10 md:h-14 md:w-14 object-contain" />
              ) : (
                <CheckCircleIcon className="h-10 w-10 md:h-14 md:w-14" style={{ color: accent_color || '#c00000' }} />
              )}
            </div>
            <div className="text-left">
              <div className="text-base md:text-xl leading-snug font-semibold">{ensureString(safeGetProp(item, 'text'))}</div>
              {safeGetProp(item, 'description') ? (
                <div className="text-sm md:text-base text-gray-600 leading-relaxed">{ensureString(safeGetProp(item, 'description'))}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </FrameWithStickyTitle>
  );
}
